import type { AjustesNevora, Etapa, EtapaPrevision } from "./tipos";

/**
 * Analítica del panel (47, Robert, 31/10/2026): por qué se pierde, qué canal funciona, mes a
 * mes, velocidad y esfuerzo, objetivos y previsión. Lo cuenta la base (nevora_panel_analisis y
 * nevora_informe_mensual); aquí, los tipos y lo que se calcula en la pantalla.
 */

/** En qué momento se pierde o se aparca una ficha (espejo de public.nevora_momento_perdida). */
export type MomentoPerdida = "llamada" | "consultoria" | "propuesta";

export const MOMENTOS: MomentoPerdida[] = [
  "llamada",
  "consultoria",
  "propuesta",
];

export const momentoPerdida = (etapa: Etapa | string | null | undefined) => {
  switch (etapa) {
    case "esperando_info":
    case "preparando_propuesta":
    case "propuesta_lista":
      return "consultoria";
    case "presentacion_agendada":
    case "pensandolo":
      return "propuesta";
    default:
      return "llamada";
  }
};

/** La lista de motivos que toca según la etapa en la que está la ficha. */
export const motivosPara = (
  ajustes: Pick<
    AjustesNevora,
    "motivosNoInteresa" | "motivosConsultoria" | "motivosPropuesta"
  >,
  etapa: Etapa | string | null | undefined,
): string[] => {
  const momento = momentoPerdida(etapa);
  if (momento === "consultoria") return ajustes.motivosConsultoria;
  if (momento === "propuesta") return ajustes.motivosPropuesta;
  return ajustes.motivosNoInteresa;
};

/** Cómo empezó la conversación (lo calcula public.nevora_cohorte). */
export type Canal =
  | "web_reserva"
  | "web_llamada"
  | "web_formulario"
  | "llamada"
  | "correo"
  | "visita"
  | "whatsapp"
  | "propio";

export const CANALES: Canal[] = [
  "web_reserva",
  "web_llamada",
  "web_formulario",
  "llamada",
  "correo",
  "visita",
  "whatsapp",
  "propio",
];

/** Conversión de un grupo de leads (canal o sector). */
export type FilaConversion = {
  entrados: number;
  contactados: number;
  citas: number;
  clientes: number;
};

export type Mediana = { n: number; mediana: number | null };

/** Lo que devuelve nevora_panel_analisis. */
export type DatosAnalisis = {
  canales: (FilaConversion & { canal: Canal })[];
  sectores: (FilaConversion & { sector: string })[];
  perdidas: { momento: MomentoPerdida; motivo: string; n: number }[];
  largo_plazo: { momento: MomentoPerdida; motivo: string; n: number }[];
  primer_contacto: { canal: Canal; n: number; mediana_min: number | null }[];
  esfuerzo: {
    llamadas: number;
    contestadas: number;
    citas: number;
    clientes: number;
  };
  dias: {
    cita: Mediana;
    realizada: Mediana;
    propuesta: Mediana;
    cliente: Mediana;
    ciclo: Mediana;
  };
  /** null si quien mira no es Jan o Robert. */
  venta_media: { n: number; media: number | null } | null;
  franjas: {
    dow: number;
    hora: number;
    llamadas: number;
    contestadas: number;
  }[];
  /** null si quien mira no es Jan o Robert. */
  prevision:
    | { etapa: EtapaPrevision; n: number; valor: number; sin_importe: number }[]
    | null;
};

/** Un mes del informe (nevora_informe_mensual). */
export type MesInforme = {
  mes: string;
  entrados: number;
  gestionados: number;
  contactados: number;
  interesados: number;
  cita: number;
  realizada: number;
  propuesta: number;
  cliente: number;
  llamadas: number;
  contestadas: number;
  citas: number;
  realizadas: number;
  no_shows: number;
  clientes: number;
  perdidos: number;
  motivo_principal: string | null;
  dias_ciclo: number | null;
  firmado: number | null;
  cobrado: number | null;
  venta_media: number | null;
};

/** Agrupa pérdidas por momento: total y motivos de más a menos. */
export const perdidasPorMomento = (filas: DatosAnalisis["perdidas"]) =>
  MOMENTOS.map((momento) => {
    const motivos = filas
      .filter((f) => f.momento === momento)
      .sort((a, b) => b.n - a.n || a.motivo.localeCompare(b.motivo, "es"));
    return {
      momento,
      total: motivos.reduce((s, f) => s + f.n, 0),
      motivos,
    };
  });

/** «12 min», «3,5 h» o «2,1 días». null → «—». */
export const textoMinutos = (min: number | null | undefined) => {
  if (min == null) return "—";
  if (min < 60) return `${Math.round(min)} min`;
  const horas = min / 60;
  if (horas < 24)
    return `${horas.toLocaleString("es-ES", { maximumFractionDigits: 1 })} h`;
  const dias = horas / 24;
  return `${dias.toLocaleString("es-ES", { maximumFractionDigits: 1 })} ${
    dias === 1 ? "día" : "días"
  }`;
};

/** «2,5 días» (o «—»). */
export const textoDias = (d: number | null | undefined) =>
  d == null
    ? "—"
    : `${d.toLocaleString("es-ES", { maximumFractionDigits: 1 })} ${
        d === 1 ? "día" : "días"
      }`;

/** Cuántas llamadas hacen falta para cada cosa (null si no hay ninguna). */
export const llamadasPor = (llamadas: number, cosas: number) =>
  cosas > 0 ? Math.round((llamadas / cosas) * 10) / 10 : null;

/** Previsión: valor de cada etapa por su probabilidad. */
export const prevision = (
  filas: NonNullable<DatosAnalisis["prevision"]>,
  probabilidades: Record<EtapaPrevision, number>,
) => {
  const lineas = filas.map((f) => {
    const pct = probabilidades[f.etapa] ?? 0;
    return { ...f, pct, ponderado: Math.round(f.valor * pct) / 100 };
  });
  return {
    lineas,
    total: lineas.reduce((s, l) => s + l.valor, 0),
    ponderado:
      Math.round(lineas.reduce((s, l) => s + l.ponderado, 0) * 100) / 100,
    sinImporte: lineas.reduce((s, l) => s + l.sin_importe, 0),
  };
};

/**
 * Progreso de un objetivo del mes: % conseguido y % del mes que ya ha pasado (para ver si se va
 * a ritmo). Sin objetivo (null o 0), null.
 */
export const progresoObjetivo = (
  valor: number,
  objetivo: number | null | undefined,
  hoy: Date,
  mes: string,
) => {
  if (!objetivo) return null;
  const [a, m] = mes.split("-").map(Number);
  const inicio = new Date(a, m - 1, 1).getTime();
  const fin = new Date(a, m, 1).getTime();
  const ahora = hoy.getTime();
  const transcurrido =
    ahora <= inicio
      ? 0
      : ahora >= fin
        ? 100
        : ((ahora - inicio) / (fin - inicio)) * 100;
  const pct = Math.round((valor / objetivo) * 100);
  return {
    pct,
    transcurrido: Math.round(transcurrido),
    aRitmo: pct >= Math.round(transcurrido),
  };
};

/** Primer día del mes de una fecha AAAA-MM-DD (o de hoy), como AAAA-MM-01. */
export const primerDiaMes = (fecha?: string) => {
  const d = fecha ? new Date(`${fecha.slice(0, 10)}T12:00:00`) : new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

/**
 * Informe mensual en CSV para Excel en español: separador «;», coma decimal y BOM (para que
 * abra bien los acentos). `columnas`: clave y título ya traducido.
 */
export const informeCsv = (
  meses: MesInforme[],
  columnas: {
    clave: keyof MesInforme | "tasa_contacto" | "tasa_cita" | "tasa_cliente";
    titulo: string;
  }[],
) => {
  const celda = (v: unknown) => {
    if (v == null) return "";
    if (typeof v === "number") return String(v).replace(".", ",");
    const s = String(v);
    return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const valor = (m: MesInforme, clave: (typeof columnas)[number]["clave"]) => {
    if (clave === "tasa_contacto") return pctONull(m.contactados, m.entrados);
    if (clave === "tasa_cita") return pctONull(m.cita, m.entrados);
    if (clave === "tasa_cliente") return pctONull(m.cliente, m.entrados);
    return m[clave];
  };
  const filas = [
    columnas.map((c) => celda(c.titulo)).join(";"),
    ...meses.map((m) =>
      columnas.map((c) => celda(valor(m, c.clave))).join(";"),
    ),
  ];
  return `\uFEFF${filas.join("\r\n")}\r\n`;
};

/** a / b en % con un decimal, o null si b es 0. */
export const pctONull = (a: number, b: number) =>
  b > 0 ? Math.round((a / b) * 1000) / 10 : null;

/** ¿La base todavía no tiene la 47? (función o vista que no existe). */
export const esAnaliticaNoDisponible = (e: unknown) =>
  /Could not find the (function|table|relation)|does not exist|PGRST20[25]|42P01|42883/i.test(
    e instanceof Error ? e.message : String(e ?? ""),
  );
